import LiveEventMetadataFields from '@/modules/live/components/live-event-metadata-fields';
import TimePicker from '@/components/timepicker/timepicker';
import { Prop, Watch } from '@Ravnur-Inc/decorators';
import sortBy from '@Ravnur-Inc/nanoutils/sortBy';
import {
  liveChannelsService,
  liveEventsRepository,
  liveSourcesService,
  liveEventTemplateRepository,
} from '../live-facade';
import LiveSplashUploader, { MAX_SPLASH_SIZE } from './live-splash-uploader';
import ProcessingSettings from './live-processing-settings';
import LiveEventFormMixin from '../mixins/live-event-form-mixin';
import { Options, setup } from 'vue-class-component';
import { required } from '@/helpers/validations.rules';
import { useVuelidate } from '@vuelidate/core';
import { LiveEvent$Metadata } from '../types/Live';
import { ACCESS_CODE_FORMATTER } from '@Ravnur-Inc/helpers/access-code';
import { LiveTemplate, LiveTemplate$Details } from '../types/LiveTemplate';
import LiveEventAttachmentRepository from '../repositories/live-event-attachment-repository';
import LiveEventAttachmentInput from './live-event-attachment-input';
import LiveEventTemplateAttachmentRepository from '../repositories/live-event-template-attachment-repository';
import useSecurityStore from '@/store/security';

import './create-live-event-modal.scss';

const CN = 'create-live-event-modal';

@Options({
  validations(this: CreateLiveEventModal) {
    const validations = {
      title: { required, $autoDirty: true },
      startDate: { required, $autoDirty: true },
      startTime: { required, $autoDirty: true },
      channelId: { required, $autoDirty: true },
      liveSourceId: { required, $autoDirty: true },

      previewAutoStartTime: {},
      liveAutoStartTime: {},
      accessCode: {},
    };

    if (this.autoStartPreview) {
      validations.previewAutoStartTime = {
        $autoDirty: true,
        required,
        previewTimeValidation: () => {
          return (this.previewAutoStartTime || 0) > (this.liveAutoStartTime || 0);
        },
      };
    }

    if (this.autoStartLive) {
      validations.liveAutoStartTime = {
        $autoDirty: true,
        required,
      };
    }

    if (this.localAccessCodeToggle) {
      validations.accessCode = {
        $autoDirty: true,
        required,
      };
    }

    return validations;
  },
})
export default class CreateLiveEventModal extends LiveEventFormMixin {
  @Prop({ type: Function as () => void, default: () => ({}) }) close?: () => void;
  @Prop({ type: String, default: null }) templateId: string;
  @Prop({ type: Boolean, default: false }) noRedirect: string;

  isTemplateNameInputVisible = false;
  templateName = '';
  templateLoading = false;
  isTemplateSaved = false;
  templates: LiveTemplate[] = [];
  selectedTemplateId: Nullable<string> = null;

  v$ = setup(() => useVuelidate());
  security = setup(() => useSecurityStore());

  private get isAutoCaptionsAvailable() {
    return this.security.isAutoCaptionsAvailable;
  }

  private get channels() {
    return liveChannelsService.list;
  }

  private get sources() {
    const { list } = liveSourcesService;
    return sortBy((s) => s.title, list);
  }

  private get isFormValid() {
    return !this.v$.$invalid;
  }

  @Watch('localAccessCodeToggle')
  onLocalAccessCodeToggleChanged() {
    if (!this.localAccessCodeToggle) {
      this.accessCode = '';
      this.v$.accessCode.$reset();
    }
  }

  @Watch('selectedTemplateId')
  async onTemplateChange() {
    if (!this.selectedTemplateId) {
      return;
    }

    try {
      const template = await liveEventTemplateRepository.get(this.selectedTemplateId);
      this.applyTemplate(template);
    } catch (e) {
      this.$processException(e);
    }
  }

  @Watch('channelId')
  protected handleChannelChanged(channelId: string) {
    const selected = this.channels.find((c) => c.id === channelId);

    this.publishImmediately = selected?.publishImmediately ?? this.publishImmediately;
    this.languageId = selected?.languageId ?? this.languageId;
    this.indexing =
      this.isAutoCaptionsAvailable && selected?.indexing ? selected?.indexing : this.indexing;
    this.notConvertToVOD = selected?.notConvertToVOD ?? this.notConvertToVOD;

    if (!this.liveSourceId && selected) {
      this.liveSourceId = selected.defaultLiveSourceId;
    }
  }

  @Watch('liveAutoStartTime')
  protected handleLiveAutoStartTimeChanged() {
    this.v$.previewAutoStartTime.$touch();
    const previewAutoStartTime = this.previewAutoStartTime;

    if (!previewAutoStartTime) {
      return;
    }

    this.previewAutoStartTime = null;
    this.$nextTick(() => {
      this.previewAutoStartTime = previewAutoStartTime;
    });
  }

  render() {
    return (
      <popup
        class={[CN, this.isTemplateNameInputVisible ? `${CN}__with-template-input` : '']}
        closeOnClickOutside={false}
        showFooter={false}
        v-slots={{ header: this.renderHeader }}
        onClose={this.close}
      >
        {this.renderTemplateSelector()}
        {this.renderEventDetailsBlock()}
        {this.renderChannelsAndSourceBlock()}
        {this.renderEventScheduleBlock()}
        {this.renderUploadsBlock()}
        {this.renderStreamingBlock()}
        {this.renderRestrictedAccessBlock()}
        {this.renderSettingsBlock()}
        {this.renderFooter()}
      </popup>
    );
  }

  private renderHeader() {
    return (
      <div class={`${CN}__header`}>
        {this.$t('live-events', 'page__create-title')}
        <a
          class={`${CN}__header-hint`}
          href="https://docs.ravnur.com/hc/en-us/articles/21493873293074-Schedule-a-Live-Event"
          rel="noreferrer"
          target="_blank"
          title={this.$t('live-events', 'create-form__header-hint')}
        >
          ?
        </a>
      </div>
    );
  }

  private renderFooter() {
    return (
      <div class={[`${CN}__footer`, this.isTemplateNameInputVisible ? `${CN}__footer--big` : '']}>
        <div class={`${CN}__footer-template-name`} v-show={this.isTemplateNameInputVisible}>
          <text-field
            requiredAsterisk
            class={`${CN}__field`}
            label={this.$t('live-events', 'field__template-name')}
            maxLength={100}
            placeholder={this.$t('live-events', 'placeholder__template-name')}
            vModel={[this.templateName, 'value']}
          />
          <r-button
            color="primary"
            disabled={!this.templateName || this.loading || this.templateLoading}
            icon="close"
            onClick={this.clearTeamplateName}
          />
          <r-button
            color="primary"
            disabled={!this.templateName || this.loading}
            icon="check"
            onClick={this.saveAsTemplate}
          />
        </div>
        <div class={`${CN}__footer-actions`}>
          {this.isTemplateSaved ? (
            <div class={`${CN}__template-saved-meesage`}>
              {this.$t('live-events', 'message__saved-as-template')}
            </div>
          ) : (
            <r-button color="primary" mode="frameless" onClick={this.toggleTemplateNameInput}>
              {this.$t('live-events', 'field__save-as-template')}
            </r-button>
          )}
          <r-button
            disabled={!this.isFormValid || this.templateLoading}
            loading={this.loading}
            onClick={this.save}
          >
            {this.$t('live-events', 'page__add-new-btn')}
          </r-button>
        </div>
      </div>
    );
  }

  private renderTemplateSelector() {
    return (
      <chosen
        class={`${CN}__field`}
        disabled={this.templates.length === 0}
        keyForLabel="templateName"
        label={this.$t('live-events', 'field__template')}
        options={this.templates}
        placeholder={
          this.templates.length === 0
            ? this.$t('live-events', 'field__no-templates')
            : this.$t('live-events', 'field__template-placeholder')
        }
        vModel={[this.selectedTemplateId, 'value']}
      />
    );
  }

  private renderEventDetailsBlock() {
    return (
      <>
        <div class={`${CN}__form-block-title`}>
          {this.$t('live-events', 'title__live-event-details')}
        </div>
        <div class={`${CN}__form-block`}>
          <text-field
            requiredAsterisk
            class={`${CN}__field`}
            errors={this.v$.title.$errors}
            label={this.$t('live-events', 'field__title')}
            maxLength={100}
            placeholder={this.$t('live-events', 'placeholder__title')}
            vModel={[this.title, 'value']}
          />

          <multiline-field
            isQuill
            class={`${CN}__field`}
            label={this.$t('live-events', 'field__description')}
            maxLength={500}
            placeholder={this.$t('live-events', 'placeholder__description')}
            rows={5}
            vModel={[this.description, 'value']}
          />

          <LiveEventMetadataFields metadata={this.metadata} onChange={this.handleMetadataSelect} />
        </div>
      </>
    );
  }

  handleMetadataSelect(metadata: LiveEvent$Metadata[]) {
    this.metadata = metadata;
  }

  private renderChannelsAndSourceBlock() {
    return (
      <>
        <div class={`${CN}__form-block-title`}>
          {this.$t('live-events', 'title__live-event-streaming')}
        </div>
        <div class={`${CN}__form-block`}>
          <chosen
            requiredAsterisk
            class={`${CN}__field`}
            errors={this.v$.channelId.$errors}
            keyForLabel="title"
            label={this.$t('live-events', 'field__channel')}
            options={this.channels}
            placeholder={this.$t('live-events', 'placeholder__channel')}
            vModel={[this.channelId, 'value']}
          />

          <chosen
            key={this.liveSourceId}
            requiredAsterisk
            class={`${CN}__field`}
            errors={this.v$.liveSourceId.$errors}
            keyForLabel="title"
            label={this.$t('live-events', 'field__live-source')}
            options={this.sources}
            placeholder={this.$t('live-events', 'placeholder__live-source')}
            vModel={[this.liveSourceId, 'value']}
          />
        </div>
      </>
    );
  }

  private renderEventScheduleBlock() {
    return (
      <>
        <div class={`${CN}__form-block-title`}>
          {this.$t('live-events', 'title__live-event-schedule')}
        </div>
        <div class={`${CN}__form-block`}>
          <datepicker
            requiredAsterisk
            class={`${CN}__field`}
            data-testid="start-date"
            errors={this.v$.startDate.$errors}
            isUtcTime={false}
            label={this.$t('live-events', 'field__start-date')}
            minDate={this.today}
            vModel={[this.startDate, 'value']}
          />

          <TimePicker
            requiredAsterisk
            class={`${CN}__field`}
            errors={this.v$.startTime.$errors}
            label={this.$t('live-events', 'field__start-time')}
            value={this.startTime}
            onSelect={this.setStartTime}
          />

          <chosen
            class={`${CN}__field`}
            label={this.$t('live-events', 'field__timezone')}
            options={this.timeZones}
            vModel={[this.timeZoneId, 'value']}
          />

          <chosen
            class={`${CN}__field`}
            label={this.$t('live-events', 'field__duration')}
            options={this.durations}
            vModel={[this.duration, 'value']}
          />
        </div>
      </>
    );
  }

  private renderUploadsBlock() {
    return (
      <>
        <div class={`${CN}__form-block-title`}>
          {this.$t('live-events', 'title__live-event-uploads')}
        </div>
        <div class={`${CN}__form-block`}>
          <LiveSplashUploader
            ref="splash"
            class={`${CN}__splash-uploader`}
            label={this.$t('live-events', 'field__splash')}
            maxSize={MAX_SPLASH_SIZE}
            removeMethod={(id: string) => liveEventsRepository.removeSplash(id)}
            src={this.splashUrl}
            uploadMethod={(file: File, id: string) => liveEventsRepository.uploadSplash(file, id)}
            onChange={this.handleSplashChanged}
          />

          <LiveEventAttachmentInput
            selectedAttachment={this.selectedAttachment}
            onAttachmentSelected={this.handleAttachmentSelected}
          />
        </div>
      </>
    );
  }

  private renderStreamingBlock() {
    return (
      <>
        <div class={`${CN}__form-block-title`}>
          {this.$t('live-events', 'title__live-event-autostart')}
        </div>
        <div class={`${CN}__form-block`}>
          <checkbox
            class={`${CN}__field`}
            label={this.$t('live-events', 'field__auto-start-preview')}
            vModel={[this.autoStartPreview, 'value']}
          />

          <chosen
            class={`${CN}__field`}
            data-testid="auto-start-preview"
            disabled={!this.autoStartPreview}
            errors={this.v$.previewAutoStartTime?.$errors}
            options={this.previewAutoStartOptions}
            v-show={this.autoStartPreview}
            vModel={[this.previewAutoStartTime, 'value']}
          />

          <checkbox
            class={`${CN}__field`}
            label={this.$t('live-events', 'field__auto-start-live')}
            vModel={[this.autoStartLive, 'value']}
          />

          <chosen
            class={`${CN}__field`}
            data-testid="auto-start-live"
            disabled={!this.autoStartLive}
            errors={this.v$.liveAutoStartTime.$errors}
            options={this.liveAutoStartOptions}
            v-show={this.autoStartLive}
            vModel={[this.liveAutoStartTime, 'value']}
          />
        </div>
      </>
    );
  }

  private renderRestrictedAccessBlock() {
    return (
      <>
        <div class={`${CN}__form-block-title`}>
          {this.$t('live-events', 'title__restricted-access')}
        </div>
        <div class={`${CN}__form-block`}>
          <checkbox
            class={`${CN}__field`}
            label={this.$t('live-events', 'field__restricted-access')}
            vModel={[this.localAccessCodeToggle, 'value']}
          />
          <text-field
            class={`${CN}__field`}
            errors={this.v$.accessCode.$errors}
            formatter={ACCESS_CODE_FORMATTER}
            label={this.$t('live-events', 'title__access-code')}
            placeholder={this.$t('live-events', 'placeholder__restricted-access')}
            v-show={this.localAccessCodeToggle}
            vModel={[this.accessCode, 'value']}
          />
        </div>
      </>
    );
  }

  private renderSettingsBlock() {
    if ((!this.isRMSLiveSource && !this.isMUXSource) || !this.isDVRSupported) {
      return;
    }

    return (
      <>
        <div class={`${CN}__form-block-title`}>
          {this.$t('live-events', 'title__live-event-post-production-settings')}
        </div>
        <div class={`${CN}__form-block`}>
          <ProcessingSettings
            settings={this.processingSettings}
            sourceId={this.liveSourceId}
            onChange={this.changeProcessingSettings}
          />
        </div>
      </>
    );
  }

  private toggleTemplateNameInput() {
    this.isTemplateNameInputVisible = !this.isTemplateNameInputVisible;
  }

  private async handleUploadsIfNeeded(entityId: string) {
    const repository = new LiveEventAttachmentRepository({ id: entityId });
    const requests = [];

    if (this.selectedAttachment instanceof File) {
      requests.push(repository.uploadAttachment(this.selectedAttachment, 'agenda'));
    }

    if (this.hasUnunploadedSplash) {
      requests.push(this.$refs.splash?.pushSplash(entityId));
    }

    if (!requests.length) {
      return;
    }

    return Promise.allSettled(requests);
  }

  private async handleTemplateUploadsIfNeeded(entityId: string) {
    const repository = new LiveEventTemplateAttachmentRepository({ id: entityId });
    const splashFile = this.$refs.splash?.file;
    const requests = [];

    if (this.selectedAttachment) {
      requests.push(repository.uploadAttachment(this.selectedAttachment as File, 'agenda'));
    }

    if (this.hasUnunploadedSplash && splashFile) {
      liveEventTemplateRepository.uploadSplash(splashFile, entityId);
    }

    if (!requests.length) {
      return;
    }

    return Promise.allSettled(requests);
  }

  applyTemplate(template: LiveTemplate$Details) {
    this.title = template.title || '';
    this.description = template.description || '';
    this.channelId = template.channelIds[0] || null; // can have only one
    this.duration = template.duration || 0;
    this.timeZoneId = template.timeZoneId;
    this.metadata = template.metadata;
    this.splashUrl = template.splashUrl;
    this.autoStartPreview = template.previewAutoStartTime !== null;
    this.previewAutoStartTime = template.previewAutoStartTime;
    this.autoStartLive = template.liveAutoStartTime !== null;
    this.liveAutoStartTime = template.liveAutoStartTime;
    this.notConvertToVOD = template.notConvertToVOD;
    this.selectedAttachment = template.attachments[0];

    // May be overwritten by channel settings so keep in nextTick
    this.$nextTick(() => {
      this.publishImmediately = template.publishImmediately;
      this.indexing = template.indexing;
      this.languageId = template.languageId;
      this.liveSourceId = template.liveSourceId;
    });
  }

  handleClearAttachment(e: Event) {
    e.preventDefault();
    this.selectedAttachment = null;
  }

  clearTeamplateName() {
    this.templateName = '';
  }

  async saveAsTemplate() {
    if (this.templateLoading || this.loading) {
      return;
    }

    const form = this.generateTemplateForm();

    try {
      this.templateLoading = true;
      const { entityId } = await liveEventTemplateRepository.save(form);

      if (entityId) {
        await this.handleTemplateUploadsIfNeeded(entityId);

        this.isTemplateSaved = true;
        this.isTemplateNameInputVisible = false;

        setTimeout(() => {
          this.isTemplateSaved = false;
        }, 5000);
      }

      await this.loadTemplates();
    } catch (e) {
      this.$processException(e);
    } finally {
      this.templateLoading = false;
    }
  }

  handleAttachmentSelected(file: File) {
    this.selectedAttachment = file;
  }

  private async save() {
    if (this.loading || this.templateLoading) {
      return;
    }

    const form = {
      ...this.generateForm(),
      channelIds: [this.channelId || ''],
      liveSourceId: this.liveSourceId || '',
    };

    try {
      this.loading = true;
      const { entityId } = await liveEventsRepository.save(form);

      if (entityId) {
        await this.handleUploadsIfNeeded(entityId);
        this.noRedirect
          ? this.handleSavedEvent()
          : this.$router.push({ name: 'LiveEventDetails', params: { id: entityId } });
      }
    } catch (e) {
      this.$processException(e);
    } finally {
      this.loading = false;
    }
  }

  handleSavedEvent() {
    this.$emit('saved');
    this.close?.();
  }

  private async loadTemplates() {
    try {
      const { items } = await liveEventTemplateRepository.load();
      this.templates = items;
    } catch (e) {
      this.$processException(e);
    }
  }

  mounted() {
    this.loadTemplates();

    if (this.templateId) {
      this.selectedTemplateId = this.templateId;
    }
  }
}
